import React, { useState, useCallback } from 'react';
import { Drawer, Input, Select, List, Avatar, Button, Row, Col, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import { formQueryStringFromObject } from 'helpers';
import { MOCK_WORKOUTS } from 'data/workoutMockData';
import { useI18n } from 'hooks/useI18n';
import {
	getWorkoutGenderOptions,
	getWorkoutLevelOptions,
	getWorkoutMuscleGroupOptions,
	translateWorkoutLevel,
	translateWorkoutMuscleGroup,
} from 'helpers/workoutI18n';

const { Search } = Input;

const getWorkoutDisplayName = (workout) =>
	workout?.name || workout?.title || workout?.workoutName || workout?.exerciseName || 'Unnamed exercise';

const getWorkoutIdentifier = (workout) => workout?.id || workout?._id || workout?.workoutId || getWorkoutDisplayName(workout);

const ExercisePickerDrawer = ({ open, onClose, onSelect }) => {
	const dispatch = useDispatch();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutsRedux = useSelector((state) => state.workoutsRedux);

	const [searchKey, setSearchKey] = useState('');
	const [muscleFilter, setMuscleFilter] = useState('');
	const [levelFilter, setLevelFilter] = useState('');
	const [genderFilter, setGenderFilter] = useState('');
	const muscleGroupOptions = getWorkoutMuscleGroupOptions(t, 'workouts.enums.muscle_group.all');
	const levelOptions = getWorkoutLevelOptions(t, 'workouts.enums.level.all');
	const genderOptions = getWorkoutGenderOptions(t, 'workouts.enums.gender.all');

	const fetchExercises = useCallback(() => {
		const params = formQueryStringFromObject({
			gender: genderFilter,
			level: levelFilter,
			muscle_group: muscleFilter,
			search: searchKey,
		});
		dispatch(getApi('GET_WORKOUTS', `${SERVER_IP}api/workouts?${params}`));
	}, [dispatch, genderFilter, levelFilter, muscleFilter, searchKey]);

	// Fetch on filter change
	React.useEffect(() => {
		if (open) fetchExercises();
	}, [open, fetchExercises]);

	// Merge redux data with mock (mock used during development)
	const allWorkouts = workoutsRedux?.list?.length ? workoutsRedux.list : MOCK_WORKOUTS;
	const displayData = allWorkouts.filter((w) => {
		const matchesMuscle = muscleFilter ? w.muscleGroup === muscleFilter : true;
		const matchesLevel = levelFilter ? w.level === levelFilter : true;
		const matchesGender = genderFilter ? w.gender === genderFilter : true;
		const matchesSearch = searchKey ? getWorkoutDisplayName(w)?.toLowerCase().includes(searchKey.toLowerCase()) : true;
		return matchesMuscle && matchesLevel && matchesGender && matchesSearch;
	});

	const loading = globalRedux.apiStatus.GET_WORKOUTS === 'PENDING';

	return (
		<Drawer
			rootClassName="template-builder-picker"
			title={t('workouts.list.add_exercise')}
			placement="right"
			width={360}
			open={open}
			onClose={onClose}
			bodyStyle={{ padding: '12px 16px' }}
		>
			<Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
				<Col span={24}>
					<Search
						placeholder={t('workouts.list.search_placeholder')}
						value={searchKey}
						onChange={(e) => setSearchKey(e.target.value)}
						allowClear
					/>
				</Col>
				<Col span={12}>
					<Select
						style={{ width: '100%' }}
						size="small"
						options={muscleGroupOptions}
						value={muscleFilter}
						onChange={setMuscleFilter}
						placeholder={t('workouts.form.fields.muscle_group')}
					/>
				</Col>
				<Col span={12}>
					<Select
						style={{ width: '100%' }}
						size="small"
						options={levelOptions}
						value={levelFilter}
						onChange={setLevelFilter}
						placeholder={t('workouts.form.fields.level')}
					/>
				</Col>
				<Col span={24}>
					<Select
						style={{ width: '100%' }}
						size="small"
						options={genderOptions}
						value={genderFilter}
						onChange={setGenderFilter}
						placeholder={t('workouts.form.fields.gender')}
					/>
				</Col>
			</Row>

			{loading ? (
				<div style={{ textAlign: 'center', paddingTop: 40 }}>
					<Spin />
				</div>
			) : (
				<List
					className="template-builder-picker__list"
					dataSource={displayData}
					renderItem={(workout) => (
						<List.Item
							key={getWorkoutIdentifier(workout)}
							className="template-builder-picker__item"
							style={{ padding: '8px 4px', cursor: 'pointer' }}
							actions={[
								<Button
									key="add"
									size="small"
									type="primary"
									icon={<PlusOutlined />}
									onClick={() => onSelect(workout)}
								/>,
							]}
						>
							<List.Item.Meta
								avatar={<Avatar style={{ background: '#4d7cfe' }}>{getWorkoutDisplayName(workout)?.[0]}</Avatar>}
								title={getWorkoutDisplayName(workout)}
								description={`${translateWorkoutMuscleGroup(t, workout.muscleGroup, workout.muscleGroup)} · ${translateWorkoutLevel(t, workout.level, workout.level)}`}
							/>
						</List.Item>
					)}
					locale={{ emptyText: t('workouts.template.picker.no_exercises_found') }}
				/>
			)}
		</Drawer>
	);
};

export default ExercisePickerDrawer;
